import type { TimestampMillis } from './common';
import type { UploadAllowedExtension } from './upload';

export const SCRIPT_ENTRY_STATUSES = ['draft', 'active', 'archived'] as const;

export type ScriptEntryStatus = (typeof SCRIPT_ENTRY_STATUSES)[number];

export const SCRIPT_PLACE_ID_LIMITS = {
  minValue: 1,
  maxValue: 999_999_999_999,
  maxDigits: 12,
  maxPerScript: 64,
  maxInputLength: 4096,
  maxNameLength: 120,
} as const;

export const SCRIPT_LOADER_LIMITS = {
  minScripts: 1,
  maxScripts: 48,
  maxTotalPayloadBytes: 16 * 1024 * 1024,
  maxNameLength: 120,
  unsupportedMessage: 'Sorry But Not Supported',
} as const;

export const SCRIPT_OBFUSCATION_PRESETS = ['standard', 'strong', 'maximum'] as const;

export type ScriptObfuscationPreset = (typeof SCRIPT_OBFUSCATION_PRESETS)[number];

export const SCRIPT_OBFUSCATION_LIMITS = {
  maxSourceBytes: 2 * 1024 * 1024,
  maxOutputBytes: 24 * 1024 * 1024,
  minSeed: 0,
  maxSeed: 4_294_967_295,
  maxWarnings: 32,
} as const;

export const PUBLIC_OBFUSCATION_LIMITS = {
  maxBodyBytes: 512 * 1024,
  maxSourceBytes: 256 * 1024,
  maxOutputBytes: 6 * 1024 * 1024,
  maxSourceCharacters: 256 * 1024,
  windowMs: 3_600_000,
  maxBuildsPerWindow: 6,
  maxWarnings: 8,
} as const;

export const SCRIPT_REJECTION_REASONS = [
  'name_invalid',
  'name_taken',
  'upload_not_found',
  'upload_not_stored',
  'script_not_found',
  'source_unreadable',
  'source_unterminated_string',
  'source_unterminated_long_string',
  'source_unterminated_long_comment',
  'source_malformed_number',
  'cursor_invalid',
  'place_ids_empty',
  'place_id_invalid',
  'place_id_taken',
  'too_many_place_ids',
  'script_ids_empty',
  'too_many_scripts',
  'payload_too_large',
  'loader_too_large',
  'source_missing',
  'protection_verification_failed',
  'storage_unavailable',
  'unexpected_field',
  'json_object_required',
] as const;

export type ScriptRejectionReason = (typeof SCRIPT_REJECTION_REASONS)[number];

export interface ScriptEntry {
  id: string;
  name: string;
  language: UploadAllowedExtension;
  status: ScriptEntryStatus;
  place_ids: number[];
  size_bytes: number;
  checksum_sha256: string;
  created_at: TimestampMillis;
  updated_at: TimestampMillis;
}

export interface ScriptListResponse {
  scripts: ScriptEntry[];
  next_cursor: string | null;
  limit: number;
  has_more: boolean;
}

export interface CreateScriptInput {
  name: string;
  upload_id: string;
  place_ids: number[] | string;
  status?: ScriptEntryStatus | undefined;
}

export interface ScriptResponse {
  script: ScriptEntry;
}

export interface UpdateScriptPlacesInput {
  place_ids: number[] | string;
}

export interface DeleteScriptResponse {
  id: string;
  removed: boolean;
}

export interface BuildLoaderInput {
  name?: string | undefined;
  script_ids: string[];
}

export interface LoaderScript {
  id: string;
  name: string;
  language: UploadAllowedExtension;
  place_ids: number[];
  size_bytes: number;
  checksum_sha256: string;
}

export interface ScriptLoader {
  name: string;
  language: UploadAllowedExtension;
  content: string;
  size_bytes: number;
  checksum_sha256: string;
  scripts: LoaderScript[];
  unsupported_message: string;
  created_at: TimestampMillis;
}

export interface BuildLoaderResponse {
  loader: ScriptLoader;
}

export interface ObfuscateScriptInput {
  script_id: string;
  preset: ScriptObfuscationPreset;
  seed?: number | undefined;
  allow_code_load?: boolean | undefined;
}

export interface ScriptProtectionStats {
  original_bytes: number;
  output_bytes: number;
  literals_encrypted: number;
  literals_skipped: number;
  table_entries: number;
  names_renamed: number;
  names_skipped: number;
  junk_blocks: number;
  helper_names: string[];
}

export interface ProtectedScript {
  id: string;
  name: string;
  language: UploadAllowedExtension;
  preset: ScriptObfuscationPreset;
  seed: number;
  content: string;
  size_bytes: number;
  checksum_sha256: string;
  stats: ScriptProtectionStats;
  warnings: string[];
  created_at: TimestampMillis;
}

export interface ObfuscateScriptResponse {
  protected_script: ProtectedScript;
}

export interface PublicObfuscateInput {
  source: string;
  preset: ScriptObfuscationPreset;
  language?: UploadAllowedExtension | undefined;
  seed?: number | undefined;
  allow_code_load?: boolean | undefined;
}

export interface PublicProtectedScript {
  name: string;
  language: UploadAllowedExtension;
  preset: ScriptObfuscationPreset;
  seed: number;
  content: string;
  size_bytes: number;
  checksum_sha256: string;
  stats: ScriptProtectionStats;
  warnings: string[];
  created_at: TimestampMillis;
}

export interface PublicObfuscateResponse {
  protected_script: PublicProtectedScript;
}

export type PlaceIdParse =
  | { ok: true; placeIds: number[] }
  | { ok: false; reason: ScriptRejectionReason; received?: string | undefined };

export const PLACE_ID_SEPARATORS = /[\s,;]+/;

export function isScriptEntryStatus(value: unknown): value is ScriptEntryStatus {
  const list: readonly string[] = SCRIPT_ENTRY_STATUSES;
  return typeof value === 'string' && list.includes(value);
}

export function isScriptObfuscationPreset(value: unknown): value is ScriptObfuscationPreset {
  const list: readonly string[] = SCRIPT_OBFUSCATION_PRESETS;
  return typeof value === 'string' && list.includes(value);
}

export function isScriptRejectionReason(value: unknown): value is ScriptRejectionReason {
  const list: readonly string[] = SCRIPT_REJECTION_REASONS;
  return typeof value === 'string' && list.includes(value);
}

export function scriptPlaceIdIsSane(value: number): boolean {
  return (
    Number.isSafeInteger(value) &&
    value >= SCRIPT_PLACE_ID_LIMITS.minValue &&
    value <= SCRIPT_PLACE_ID_LIMITS.maxValue
  );
}

function placeIdToken(token: string): number | null {
  if (token.length === 0 || token.length > SCRIPT_PLACE_ID_LIMITS.maxDigits) {
    return null;
  }
  for (const character of token) {
    if (character < '0' || character > '9') {
      return null;
    }
  }
  const value = Number.parseInt(token, 10);
  return scriptPlaceIdIsSane(value) ? value : null;
}

export function parsePlaceIds(value: readonly unknown[] | string): PlaceIdParse {
  const tokens: string[] = [];
  if (typeof value === 'string') {
    if (value.length > SCRIPT_PLACE_ID_LIMITS.maxInputLength) {
      return { ok: false, reason: 'too_many_place_ids', received: 'input_too_long' };
    }
    for (const token of value.split(PLACE_ID_SEPARATORS)) {
      if (token !== '') {
        tokens.push(token);
      }
    }
  } else {
    for (const entry of value) {
      if (typeof entry === 'number') {
        if (scriptPlaceIdIsSane(entry)) {
          tokens.push(String(entry));
        } else {
          return { ok: false, reason: 'place_id_invalid', received: String(entry) };
        }
      } else if (typeof entry === 'string') {
        for (const token of entry.split(PLACE_ID_SEPARATORS)) {
          if (token !== '') {
            tokens.push(token);
          }
        }
      } else {
        return { ok: false, reason: 'place_id_invalid' };
      }
    }
  }

  if (tokens.length === 0) {
    return { ok: false, reason: 'place_ids_empty' };
  }

  const seen = new Set<number>();
  const placeIds: number[] = [];
  for (const token of tokens) {
    const parsed = placeIdToken(token);
    if (parsed === null) {
      return { ok: false, reason: 'place_id_invalid', received: token };
    }
    if (seen.has(parsed)) {
      continue;
    }
    seen.add(parsed);
    placeIds.push(parsed);
    if (placeIds.length > SCRIPT_PLACE_ID_LIMITS.maxPerScript) {
      return { ok: false, reason: 'too_many_place_ids', received: String(placeIds.length) };
    }
  }

  return { ok: true, placeIds };
}

export function placeIdsPreview(placeIds: readonly number[], max = 6): string {
  if (placeIds.length === 0) {
    return '';
  }
  const head = placeIds.slice(0, max).map(String).join(', ');
  const rest = placeIds.length - max;
  return rest > 0 ? `${head} +${rest}` : head;
}

export function isScriptLoaderNameValid(name: string): boolean {
  const trimmed = name.trim();
  return (
    trimmed === name &&
    trimmed.length > 0 &&
    trimmed.length <= SCRIPT_LOADER_LIMITS.maxNameLength &&
    !name.includes('\u0000')
  );
}

export function isScriptEntryNameValid(name: string): boolean {
  const trimmed = name.trim();
  if (
    trimmed !== name ||
    trimmed.length === 0 ||
    trimmed.length > SCRIPT_PLACE_ID_LIMITS.maxNameLength
  ) {
    return false;
  }
  for (const character of name) {
    const code = character.charCodeAt(0);
    if (code < 32 || code === 127) {
      return false;
    }
  }
  return true;
}
